import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  getAdminSettings,
  ensureAdminSettings,
  updateStoreSettings,
  updateBusinessHours,
  updateFiscalSettings,
  updateNotificationSettings,
  updateSocialMediaSettings,
  updateFinancingSettings,
  updateWarrantySettings,
} from "@/services/adminSettingsService";
import type {
  StoreSettings,
  BusinessHours,
  FiscalSettings,
  NotificationSettings,
  SocialMediaSettings,
  FinancingSettingsForm,
  WarrantySettings,
} from "@/types/adminSettings";
import { DAY_LABELS } from "@/types/adminSettings";
import { queryKeys } from "@/lib/queryKeys";
import {
  Store,
  Clock,
  CreditCard,
  FileText,
  Bell,
  Share2,
  Save,
  Loader2,
  ShieldCheck,
} from "lucide-react";
import { toast } from "sonner";

export function Configuracion() {
  const queryClient = useQueryClient();
  const { data: settings, isLoading: loadingSettings } = useQuery({
    queryKey: queryKeys.adminSettings,
    queryFn: getAdminSettings,
  });

  const [store, setStore] = useState<StoreSettings | null>(null);
  const [fiscal, setFiscal] = useState<FiscalSettings | null>(null);
  const [notifications, setNotifications] = useState<NotificationSettings | null>(null);
  const [socialMedia, setSocialMedia] = useState<SocialMediaSettings | null>(null);
  const [financing, setFinancing] = useState<FinancingSettingsForm | null>(null);
  const [warranty, setWarranty] = useState<WarrantySettings | null>(null);

  const [saving, setSaving] = useState<string | null>(null);

  useEffect(() => {
    if (settings?.store) setStore(settings.store);
    if (settings?.fiscal) setFiscal(settings.fiscal);
    if (settings?.notifications) setNotifications(settings.notifications);
    if (settings?.socialMedia) setSocialMedia(settings.socialMedia);
    if (settings?.financing) {
      const f = settings.financing as FinancingSettingsForm & Record<string, unknown>;
      setFinancing({
        isEnabled: f.isEnabled ?? true,
        requiresApproval: f.requiresApproval ?? true,
        maxFinancingAmount: f.maxFinancingAmount ?? 500000,
        minCreditScore: f.minCreditScore ?? 650,
        baseInterestRate: f.baseInterestRate ?? 18.5,
      });
    }
    if (settings?.warranty) {
      const w = settings.warranty as WarrantySettings;
      setWarranty({
        moduleEnabled: w.moduleEnabled ?? true,
        defaultWarrantyText: w.defaultWarrantyText ?? "1 año o 20,000 km",
        warrantyContactEmail: w.warrantyContactEmail ?? "",
        warrantyContactPhone: w.warrantyContactPhone ?? "",
      });
    } else {
      setWarranty({
        moduleEnabled: true,
        defaultWarrantyText: "1 año o 20,000 km",
        warrantyContactEmail: "",
        warrantyContactPhone: "",
      });
    }
  }, [settings]);

  const handleInit = async () => {
    setSaving("init");
    try {
      await ensureAdminSettings();
      await queryClient.invalidateQueries({ queryKey: queryKeys.adminSettings });
      toast.success("Configuración inicializada");
    } catch {
      toast.error("Error al inicializar");
    } finally {
      setSaving(null);
    }
  };

  const saveStore = async () => {
    if (!store) return;
    setSaving("store");
    try {
      await updateStoreSettings(store);
      await queryClient.invalidateQueries({ queryKey: queryKeys.adminSettings });
      toast.success("Tienda guardada");
    } catch {
      toast.error("Error al guardar tienda");
    } finally {
      setSaving(null);
    }
  };

  const saveHours = async () => {
    if (!store?.businessHours) return;
    setSaving("hours");
    try {
      await updateBusinessHours(store.businessHours);
      await queryClient.invalidateQueries({ queryKey: queryKeys.adminSettings });
      toast.success("Horarios guardados");
    } catch {
      toast.error("Error al guardar horarios");
    } finally {
      setSaving(null);
    }
  };

  const saveFiscal = async () => {
    if (!fiscal) return;
    setSaving("fiscal");
    try {
      await updateFiscalSettings(fiscal);
      await queryClient.invalidateQueries({ queryKey: queryKeys.adminSettings });
      toast.success("Datos legales guardados");
    } catch {
      toast.error("Error al guardar");
    } finally {
      setSaving(null);
    }
  };

  const saveNotifications = async () => {
    if (!notifications) return;
    setSaving("notifications");
    try {
      await updateNotificationSettings(notifications);
      await queryClient.invalidateQueries({ queryKey: queryKeys.adminSettings });
      toast.success("Notificaciones guardadas");
    } catch {
      toast.error("Error al guardar");
    } finally {
      setSaving(null);
    }
  };

  const saveSocial = async () => {
    if (!socialMedia) return;
    setSaving("social");
    try {
      await updateSocialMediaSettings(socialMedia);
      await queryClient.invalidateQueries({ queryKey: queryKeys.adminSettings });
      toast.success("Redes sociales guardadas");
    } catch {
      toast.error("Error al guardar");
    } finally {
      setSaving(null);
    }
  };

  const saveFinancing = async () => {
    if (!financing) return;
    setSaving("financing");
    try {
      await updateFinancingSettings(financing);
      await queryClient.invalidateQueries({ queryKey: queryKeys.adminSettings });
      toast.success("Financiamiento guardado");
    } catch {
      toast.error("Error al guardar");
    } finally {
      setSaving(null);
    }
  };

  const saveWarranty = async () => {
    if (!warranty) return;
    setSaving("warranty");
    try {
      await updateWarrantySettings(warranty);
      await queryClient.invalidateQueries({ queryKey: queryKeys.adminSettings });
      toast.success("Configuración de garantías guardada");
    } catch {
      toast.error("Error al guardar");
    } finally {
      setSaving(null);
    }
  };

  const updateHours = (day: keyof BusinessHours, patch: Partial<StoreSettings["businessHours"]["monday"]>) => {
    if (!store?.businessHours) return;
    setStore({
      ...store,
      businessHours: {
        ...store.businessHours,
        [day]: { ...store.businessHours[day], ...patch },
      },
    });
  };

  if (loadingSettings && !settings) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!settings) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="font-display text-2xl font-semibold">Configuración</h1>
          <p className="text-muted-foreground">
            Configuración global de la app móvil (tienda, horarios, financiamiento, legal, notificaciones, redes).
          </p>
        </div>
        <Card>
          <CardContent className="flex flex-col items-center gap-4 py-12">
            <p className="text-muted-foreground">Aún no hay configuración. Inicializa para usar el mismo documento que la app móvil.</p>
            <Button onClick={handleInit} disabled={saving === "init"}>
              {saving === "init" ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              Inicializar configuración
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const busy = saving !== null;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-2xl font-semibold">Configuración</h1>
        <p className="text-muted-foreground">
          Misma configuración que la app móvil (admin). Cambios aquí se reflejan en la app.
        </p>
      </div>

      <Tabs defaultValue="store" className="space-y-4">
        <TabsList className="flex flex-wrap gap-1">
          <TabsTrigger value="store" className="gap-2">
            <Store className="h-4 w-4" />
            Tienda
          </TabsTrigger>
          <TabsTrigger value="hours" className="gap-2">
            <Clock className="h-4 w-4" />
            Horarios
          </TabsTrigger>
          <TabsTrigger value="financing" className="gap-2">
            <CreditCard className="h-4 w-4" />
            Financiamiento
          </TabsTrigger>
          <TabsTrigger value="legal" className="gap-2">
            <FileText className="h-4 w-4" />
            Legal
          </TabsTrigger>
          <TabsTrigger value="notifications" className="gap-2">
            <Bell className="h-4 w-4" />
            Notificaciones
          </TabsTrigger>
          <TabsTrigger value="social" className="gap-2">
            <Share2 className="h-4 w-4" />
            Redes
          </TabsTrigger>
          <TabsTrigger value="warranty" className="gap-2">
            <ShieldCheck className="h-4 w-4" />
            Garantías
          </TabsTrigger>
        </TabsList>

        <TabsContent value="store" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Información de la tienda</CardTitle>
              <CardDescription>Nombre, contacto y dirección. Se muestra en la app.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {store && (
                <>
                  <div className="grid gap-2">
                    <Label>Nombre de la tienda</Label>
                    <Input
                      value={store.storeName}
                      onChange={(e) => setStore({ ...store, storeName: e.target.value })}
                      placeholder="Oriental Ramírez Motors"
                    />
                  </div>
                  <div className="grid gap-2">
                    <Label>Teléfono</Label>
                    <Input
                      value={store.storePhone}
                      onChange={(e) => setStore({ ...store, storePhone: e.target.value })}
                      placeholder="+1 (809) 123-4567"
                    />
                  </div>
                  <div className="grid gap-2">
                    <Label>Email</Label>
                    <Input
                      type="email"
                      value={store.storeEmail}
                      onChange={(e) => setStore({ ...store, storeEmail: e.target.value })}
                      placeholder="info@ejemplo.com"
                    />
                  </div>
                  <div className="grid gap-2">
                    <Label>Dirección</Label>
                    <Input
                      value={store.storeAddress ?? ""}
                      onChange={(e) => setStore({ ...store, storeAddress: e.target.value })}
                      placeholder="Av. Principal 123"
                    />
                  </div>
                  <div className="grid gap-2">
                    <Label>Ciudad</Label>
                    <Input
                      value={store.storeCity ?? ""}
                      onChange={(e) => setStore({ ...store, storeCity: e.target.value })}
                      placeholder="San Pedro de Macorís"
                    />
                  </div>
                  <Button onClick={saveStore} disabled={busy} className="gap-2">
                    {saving === "store" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                    {saving === "store" ? "Guardando…" : "Guardar tienda"}
                  </Button>
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="hours" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Horario de atención</CardTitle>
              <CardDescription>Horarios por día. Formato 24h (ej. 09:00, 18:00).</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              {store?.businessHours &&
                (Object.entries(store.businessHours) as [keyof BusinessHours, (typeof store.businessHours)[keyof BusinessHours]][]).map(
                  ([day, hours]) => (
                    <div
                      key={day}
                      className="flex flex-wrap items-center justify-between gap-2 rounded-lg border p-3"
                    >
                      <span className="font-medium">{DAY_LABELS[day]}</span>
                      <div className="flex items-center gap-2">
                        <Switch
                          checked={!hours.closed}
                          onCheckedChange={(v) => updateHours(day, { closed: !v })}
                        />
                        {!hours.closed && (
                          <>
                            <Input
                              className="w-20"
                              value={hours.open}
                              onChange={(e) => updateHours(day, { open: e.target.value })}
                              placeholder="09:00"
                            />
                            <span className="text-muted-foreground">–</span>
                            <Input
                              className="w-20"
                              value={hours.close}
                              onChange={(e) => updateHours(day, { close: e.target.value })}
                              placeholder="18:00"
                            />
                          </>
                        )}
                        {hours.closed && <span className="text-sm text-muted-foreground">Cerrado</span>}
                      </div>
                    </div>
                  )
                )}
              <Button onClick={saveHours} disabled={busy} className="gap-2">
                {saving === "hours" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                {saving === "hours" ? "Guardando…" : "Guardar horarios"}
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="financing" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Financiamiento</CardTitle>
              <CardDescription>Habilitar solicitudes y límites. La app usa estos valores.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {financing && (
                <>
                  <div className="flex items-center justify-between rounded-lg border p-4">
                    <div>
                      <Label>Financiamiento habilitado</Label>
                      <p className="text-sm text-muted-foreground">Permitir solicitudes en la app</p>
                    </div>
                    <Switch
                      checked={financing.isEnabled}
                      onCheckedChange={(v) => setFinancing({ ...financing, isEnabled: v })}
                    />
                  </div>
                  <div className="flex items-center justify-between rounded-lg border p-4">
                    <div>
                      <Label>Requiere aprobación</Label>
                      <p className="text-sm text-muted-foreground">Cada solicitud debe ser aprobada</p>
                    </div>
                    <Switch
                      checked={financing.requiresApproval}
                      onCheckedChange={(v) => setFinancing({ ...financing, requiresApproval: v })}
                    />
                  </div>
                  <div className="grid gap-2">
                    <Label>Monto máximo (RD$)</Label>
                    <Input
                      type="number"
                      value={financing.maxFinancingAmount}
                      onChange={(e) =>
                        setFinancing({ ...financing, maxFinancingAmount: Number(e.target.value) || 0 })
                      }
                    />
                  </div>
                  <div className="grid gap-2">
                    <Label>Puntuación mínima de crédito</Label>
                    <Input
                      type="number"
                      value={financing.minCreditScore ?? ""}
                      onChange={(e) =>
                        setFinancing({ ...financing, minCreditScore: Number(e.target.value) || undefined })
                      }
                      placeholder="650"
                    />
                  </div>
                  <div className="grid gap-2">
                    <Label>Tasa de interés base (%)</Label>
                    <Input
                      type="number"
                      step="0.1"
                      value={financing.baseInterestRate ?? ""}
                      onChange={(e) =>
                        setFinancing({ ...financing, baseInterestRate: Number(e.target.value) || undefined })
                      }
                      placeholder="18.5"
                    />
                  </div>
                  <Button onClick={saveFinancing} disabled={busy} className="gap-2">
                    {saving === "financing" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                    {saving === "financing" ? "Guardando…" : "Guardar financiamiento"}
                  </Button>
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="legal" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Información legal / fiscal</CardTitle>
              <CardDescription>RNC y razón social. Para facturación y legal en la app.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {fiscal && (
                <>
                  <div className="grid gap-2">
                    <Label>RNC</Label>
                    <Input
                      value={fiscal.rnc}
                      onChange={(e) => setFiscal({ ...fiscal, rnc: e.target.value })}
                      placeholder="101-12345-6"
                    />
                  </div>
                  <div className="grid gap-2">
                    <Label>Nombre de la empresa</Label>
                    <Input
                      value={fiscal.companyName ?? ""}
                      onChange={(e) => setFiscal({ ...fiscal, companyName: e.target.value })}
                      placeholder="Oriental Ramírez Motors SRL"
                    />
                  </div>
                  <div className="grid gap-2">
                    <Label>Dirección fiscal (opcional)</Label>
                    <Input
                      value={fiscal.companyAddress ?? ""}
                      onChange={(e) => setFiscal({ ...fiscal, companyAddress: e.target.value })}
                      placeholder="Av. Principal 123"
                    />
                  </div>
                  <Button onClick={saveFiscal} disabled={busy} className="gap-2">
                    {saving === "fiscal" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                    {saving === "fiscal" ? "Guardando…" : "Guardar datos legales"}
                  </Button>
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="notifications" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Notificaciones</CardTitle>
              <CardDescription>Canales que la app usa para notificar (email, WhatsApp, push).</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {notifications && (
                <>
                  <div className="flex items-center justify-between rounded-lg border p-4">
                    <div>
                      <Label>Email</Label>
                      <p className="text-sm text-muted-foreground">Notificaciones por correo</p>
                    </div>
                    <Switch
                      checked={notifications.emailNotifications}
                      onCheckedChange={(v) => setNotifications({ ...notifications, emailNotifications: v })}
                    />
                  </div>
                  <div className="flex items-center justify-between rounded-lg border p-4">
                    <div>
                      <Label>WhatsApp</Label>
                      <p className="text-sm text-muted-foreground">Notificaciones por WhatsApp</p>
                    </div>
                    <Switch
                      checked={notifications.whatsappNotifications}
                      onCheckedChange={(v) => setNotifications({ ...notifications, whatsappNotifications: v })}
                    />
                  </div>
                  <div className="flex items-center justify-between rounded-lg border p-4">
                    <div>
                      <Label>Push (app)</Label>
                      <p className="text-sm text-muted-foreground">Notificaciones en la app</p>
                    </div>
                    <Switch
                      checked={notifications.pushNotifications}
                      onCheckedChange={(v) => setNotifications({ ...notifications, pushNotifications: v })}
                    />
                  </div>
                  <Button onClick={saveNotifications} disabled={busy} className="gap-2">
                    {saving === "notifications" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                    {saving === "notifications" ? "Guardando…" : "Guardar notificaciones"}
                  </Button>
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="social" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Redes sociales</CardTitle>
              <CardDescription>Enlaces que la app puede mostrar (Facebook, Instagram, WhatsApp, etc.).</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {socialMedia && (
                <>
                  {["facebook", "instagram", "whatsapp", "twitter", "youtube"].map((key) => (
                    <div key={key} className="grid gap-2">
                      <Label className="capitalize">{key}</Label>
                      <Input
                        value={socialMedia[key as keyof SocialMediaSettings] ?? ""}
                        onChange={(e) =>
                          setSocialMedia({ ...socialMedia, [key]: e.target.value })
                        }
                        placeholder={key === "whatsapp" ? "+1 (809) 123-4567" : `https://${key}.com/...`}
                      />
                    </div>
                  ))}
                  <Button onClick={saveSocial} disabled={busy} className="gap-2">
                    {saving === "social" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                    {saving === "social" ? "Guardando…" : "Guardar redes"}
                  </Button>
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="warranty" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Configuración de garantías</CardTitle>
              <CardDescription>
                Módulo de garantía, texto por defecto y contacto. Se guarda en la misma configuración que la app.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {warranty && (
                <>
                  <div className="flex items-center justify-between rounded-lg border p-4">
                    <div>
                      <Label className="text-base font-medium">Módulo de garantía activo</Label>
                      <p className="text-sm text-muted-foreground">
                        Los vehículos y repuestos podrán asociar garantías según los calendarios definidos.
                      </p>
                    </div>
                    <Switch
                      checked={warranty.moduleEnabled}
                      onCheckedChange={(v) => setWarranty({ ...warranty, moduleEnabled: v })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="default-warranty">Texto de garantía por defecto</Label>
                    <Input
                      id="default-warranty"
                      value={warranty.defaultWarrantyText}
                      onChange={(e) => setWarranty({ ...warranty, defaultWarrantyText: e.target.value })}
                      placeholder="Ej. 1 año o 20,000 km"
                    />
                    <p className="text-xs text-muted-foreground">
                      Sugerencia mostrada en el formulario de vehículos cuando el campo está vacío.
                    </p>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="warranty-email">Email de contacto (garantías)</Label>
                    <Input
                      id="warranty-email"
                      type="email"
                      value={warranty.warrantyContactEmail}
                      onChange={(e) => setWarranty({ ...warranty, warrantyContactEmail: e.target.value })}
                      placeholder="garantias@ejemplo.com"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="warranty-phone">Teléfono de contacto (garantías)</Label>
                    <Input
                      id="warranty-phone"
                      value={warranty.warrantyContactPhone}
                      onChange={(e) => setWarranty({ ...warranty, warrantyContactPhone: e.target.value })}
                      placeholder="+1 809 000 0000"
                    />
                  </div>
                  <Button onClick={saveWarranty} disabled={busy} className="gap-2">
                    {saving === "warranty" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                    {saving === "warranty" ? "Guardando…" : "Guardar configuración de garantías"}
                  </Button>
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
